import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ProfileService, HANDLE_RE, BIO_MAX, DISPLAY_NAME_MAX, INSTAGRAM_MAX, profileLabel } from '../services/profile-service.js';
import { CollectionService } from '../services/collection-service.js';
import { BlockService } from '../services/block-service.js';
import { FollowButton } from '../components/FollowButton.jsx';
import { FollowListModal } from '../components/FollowListModal.jsx';
import { CollectionCard } from '../components/CollectionCard.jsx';
import { QueryCache } from '../services/cache.js';
import { shareLink } from '../services/share-service.js';
import { publicOrigin } from '../services/platform-service.js';
import { useLocale } from '../hooks/useLocale.jsx';
import { GetTheAppBanner } from '../components/GetTheAppBanner.jsx';
import { gridImage, fallbackToFull } from '../utils/design-image.js';

export function Profile({ user, onSignInRequest }) {
  const { handle } = useParams();
  const navigate = useNavigate();
  const { t, styleName } = useLocale();

  const cachedProfile = handle ? QueryCache.get(`profile:handle:${handle}`) : null;
  const [profile, setProfile] = useState(cachedProfile || null);
  const [designs, setDesigns] = useState(
    cachedProfile ? (QueryCache.get(`userListedDesigns:${cachedProfile.uid}`) || []) : []
  );
  const [collections, setCollections] = useState(
    cachedProfile ? (QueryCache.get(`profileCollections:${cachedProfile.uid}`) || []) : []
  );
  // 더 보기 커서. 캐시로 그린 첫 화면엔 커서가 없으므로 fetch 가 끝나야
  // 버튼이 뜬다 (캐시는 첫 페이지만 담는다).
  const [designsCursor, setDesignsCursor] = useState(null);
  const [hasMoreDesigns, setHasMoreDesigns] = useState(false);
  const [loadingMoreDesigns, setLoadingMoreDesigns] = useState(false);
  const [tab, setTab] = useState('designs');
  const [loading, setLoading] = useState(!cachedProfile);
  const [notFound, setNotFound] = useState(false);

  // Followers/Following modal — null = closed, 'followers'|'following' = open with that tab.
  const [followListTab, setFollowListTab] = useState(null);

  // Block 상태 — realtime.
  const [isBlocked, setIsBlocked] = useState(false);

  // Self-edit state — only used when isSelf.
  const [isEditing, setIsEditing] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [handleInput, setHandleInput] = useState('');
  const [bioInput, setBioInput] = useState('');
  const [instagramInput, setInstagramInput] = useState('');
  const [editStatus, setEditStatus] = useState({ kind: 'idle' }); // idle | pending | error

  // 프로필 공유 — idle | copied. shareLink 가 native share sheet 를 못 쓰면
  // 클립보드로 폴백하고 false 를 돌려주므로, 그때 "복사됨" 을 잠깐 보여준다.
  const [shareState, setShareState] = useState('idle');

  useEffect(() => {
    let mounted = true;
    let unsubLive = () => {};
    if (!cachedProfile) {
      setLoading(true);
      setNotFound(false);
    }
    ProfileService.getByHandle(handle).then(async (p) => {
      if (!mounted) return;
      if (!p) { setNotFound(true); setLoading(false); return; }
      setProfile(p);
      QueryCache.set(`profile:handle:${handle}`, p);
      QueryCache.set(`profile:uid:${p.uid}`, p);
      unsubLive = ProfileService.subscribeByUid(p.uid, (live) => {
        if (mounted && live) {
          setProfile(live);
          QueryCache.set(`profile:handle:${handle}`, live);
          QueryCache.set(`profile:uid:${live.uid}`, live);
        }
      });
      const { designs: ds, lastVisible, hasMore } = await ProfileService.getDesignsByUid(p.uid);
      if (mounted) {
        setDesigns(ds);
        setDesignsCursor(lastVisible);
        setHasMoreDesigns(hasMore);
        QueryCache.set(`userListedDesigns:${p.uid}`, ds);
      }
      // 본인/타인 분기 없음 — 프로필에 보이는 건 누가 보든 같다. 갈라놓으면
      // 공유한 사람은 10개를 보는데 받은 사람은 2개만 보이는 상황이 생긴다.
      const cs = await CollectionService.listByOwner(p.uid);
      if (mounted) {
        setCollections(cs);
        QueryCache.set(`profileCollections:${p.uid}`, cs);
        setLoading(false);
      }
    }).catch(() => {
      if (mounted && !cachedProfile) { setNotFound(true); setLoading(false); }
    });
    return () => { mounted = false; unsubLive?.(); };
  }, [handle]); // eslint-disable-line

  const loadMoreDesigns = async () => {
    if (!profile?.uid || !designsCursor || loadingMoreDesigns) return;
    setLoadingMoreDesigns(true);
    try {
      const { designs: more, lastVisible, hasMore } = await ProfileService.getDesignsByUid(profile.uid, { lastDoc: designsCursor });
      setDesigns(prev => [...prev, ...more]);
      setDesignsCursor(lastVisible);
      setHasMoreDesigns(hasMore);
    } catch (e) {
      console.warn('profile load more:', e);
    } finally {
      setLoadingMoreDesigns(false);
    }
  };

  // 차단 상태 실시간 구독. self / anon 은 자동으로 false.
  useEffect(() => {
    if (!profile?.uid) return undefined;
    return BlockService.subscribeIsBlocked(profile.uid, setIsBlocked);
  }, [profile?.uid]);

  const handleBlockToggle = async () => {
    if (!user || user.isAnonymous) { onSignInRequest?.(); return; }
    if (!isBlocked) {
      const msg = t('blockConfirm', { handle: profile.handle });
      if (!window.confirm(msg)) return;
    }
    try {
      await BlockService.toggleBlock(profile.uid, isBlocked);
    } catch (err) {
      console.warn('block toggle failed:', err);
      window.alert(t('errBlockGeneric'));
    }
  };

  const handleShareProfile = async () => {
    if (!profile?.handle) return;
    const url = `${publicOrigin()}/u/${profile.handle}`;
    try {
      const shared = await shareLink({
        title: profileLabel(profile),
        text: t('profileShareText', { name: profileLabel(profile) }),
        url,
      });
      if (!shared) {
        setShareState('copied');
        setTimeout(() => setShareState('idle'), 1800);
      }
    } catch {
      // 사용자가 share sheet 를 닫음 — 아무 것도 안 함.
    }
  };

  // Reset edit inputs when entering edit mode.
  const startEditing = () => {
    setNameInput(profile?.displayName || user?.displayName || '');
    setHandleInput(profile?.handle || '');
    setBioInput(profile?.bio || '');
    setInstagramInput(profile?.instagram || '');
    setEditStatus({ kind: 'idle' });
    setIsEditing(true);
  };

  const saveAll = async () => {
    setEditStatus({ kind: 'pending' });
    try {
      // 1) handle (if changed). Format check first.
      const desiredHandle = handleInput.trim().toLowerCase();
      if (desiredHandle && desiredHandle !== profile.handle) {
        if (!HANDLE_RE.test(desiredHandle)) {
          setEditStatus({ kind: 'error', message: t('handleInvalid') });
          return;
        }
        await ProfileService.claimHandle(desiredHandle);
      }
      // 2) display name (if changed)
      if (nameInput !== (profile?.displayName || user?.displayName || '')) {
        await ProfileService.updateDisplayName(nameInput);
      }
      // 3) bio (if changed)
      if (bioInput !== (profile?.bio || '')) {
        await ProfileService.updateBio(bioInput);
      }
      // 4) instagram (if changed) — server normalizes (@/URL/slash 제거).
      if (instagramInput !== (profile?.instagram || '')) {
        await ProfileService.updateInstagram(instagramInput);
      }
      setIsEditing(false);
      setEditStatus({ kind: 'idle' });
      // 핸들이 바뀌었으면 새 URL 로 이동
      if (desiredHandle && desiredHandle !== profile.handle) {
        navigate(`/u/${desiredHandle}`, { replace: true });
      }
    } catch (err) {
      const msg = err.code === 'HANDLE_TAKEN' ? t('handleTaken')
        : err.code === 'INVALID_HANDLE' ? t('handleInvalid')
        : err.code === 'INVALID_INSTAGRAM' ? t('instagramInvalid')
        : t('handleErrGeneric');
      setEditStatus({ kind: 'error', message: msg });
    }
  };

  if (loading) return <main className="main"><div className="loading"><div className="spinner"></div></div></main>;
  if (notFound) return <main className="main"><p style={{ color: 'var(--text-secondary)' }}>{t('profileNotFound')}</p></main>;
  if (!profile) return null;

  const isSelf = user && !user.isAnonymous && user.uid === profile.uid;

  return (
    <main className="main">
      <GetTheAppBanner surface="profile" />
      <div className="profile-header" style={{ position: 'relative' }}>
        {!isSelf && !isEditing && (
          <button
            type="button"
            className="profile-block-btn"
            aria-label={isBlocked ? t('unblockUser') : t('blockUser')}
            title={isBlocked ? t('unblockUser') : t('blockUser')}
            onClick={handleBlockToggle}
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.5rem',
              color: isBlocked ? 'var(--text-danger, #b5654a)' : 'var(--text-muted)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <i className="material-symbols-rounded">block</i>
          </button>
        )}
        {profile.photoURL ? (
          <img src={profile.photoURL} alt="" className="profile-avatar" />
        ) : (
          <div className="profile-avatar profile-avatar-placeholder"><i className="material-symbols-rounded">person</i></div>
        )}
        <div className="profile-meta">
          {!isEditing ? (
            <>
              {/* Instagram 식 — Profile 헤더는 큰 단독 컨텍스트라 bare handle (@ 생략).
                  displayName 은 custom 일 때만 부제목. */}
              <h2 className="profile-name">{profile.handle}</h2>
              {profile.displayName && profile.displayName !== 'archelier user' && (
                <p className="profile-handle">{profile.displayName}</p>
              )}
              {profile.bio && <p className="profile-bio">{profile.bio}</p>}
              {profile.instagram && (
                <a
                  className="profile-instagram"
                  href={`https://instagram.com/${profile.instagram}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg className="profile-instagram-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" d="M7 3h10a4 4 0 0 1 4 4v10a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V7a4 4 0 0 1 4-4z"/>
                    <circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" strokeWidth="1.8"/>
                    <circle cx="17.5" cy="6.5" r="1.1" fill="currentColor"/>
                  </svg>
                  <span>@{profile.instagram}</span>
                </a>
              )}
              {/* Designs / Followers / Following 카운트는 감춰 뒀다 — 공개
                 피드를 접어서 팔로우가 실제로 하는 일이 없어졌고, 0 이 세 개
                 나란히 뜨는 게 오히려 빈 앱처럼 보였다. FollowButton /
                 FollowListModal / 서버 카운터 트리거는 그대로 살아 있으니
                 되살릴 땐 이 블록만 복구하면 된다. */}
              <div className="profile-actions">
                {isSelf ? (
                  <button className="btn btn-secondary" onClick={startEditing}>
                    <i className="material-symbols-rounded">edit</i>
                    {t('profileEdit')}
                  </button>
                ) : !isBlocked && (
                  <FollowButton targetUid={profile.uid} user={user} onSignInRequest={onSignInRequest} size="md" />
                )}
                {/* 공개 피드를 접은 대신 프로필 자체를 공유할 수 있게 — 이제
                   남에게 내 작업을 보여주는 경로는 디자인 하나(/s/:id) 아니면
                   프로필 통째로(/u/:handle) 둘뿐이다. */}
                <button className="btn btn-secondary" onClick={handleShareProfile}>
                  <i className="material-symbols-rounded">{shareState === 'copied' ? 'check' : 'share'}</i>
                  {shareState === 'copied' ? t('copied') : t('profileShare')}
                </button>
              </div>
            </>
          ) : (
            <div className="profile-edit-form">
              <label className="edit-field-label">{t('displayNameLabel')}</label>
              <input
                className="edit-field-input"
                value={nameInput}
                onChange={e => setNameInput(e.target.value.slice(0, DISPLAY_NAME_MAX))}
                placeholder={t('displayNamePlaceholder')}
                maxLength={DISPLAY_NAME_MAX}
              />

              {/* 핸들 편집은 감춰 뒀다 — 공개 피드를 접으면서 남에게 검색당할
                 일이 없어졌고, 서버가 가입 시 자동으로 붙여 주는 값 (profile.js
                 ensureProfile) 으로 충분하다. 프로필 URL 은 계속 그 핸들을 쓴다.
                 handleInput 은 startEditing 이 현재 핸들로 채워 두므로 saveAll
                 의 "바뀌었으면 claimHandle" 분기는 자연히 건너뛴다.
                 되살리려면 아래 input 주석만 풀면 된다. */}

              <label className="edit-field-label">{t('profileBio')}</label>
              <textarea
                className="edit-field-input edit-field-textarea"
                value={bioInput}
                onChange={e => setBioInput(e.target.value.slice(0, BIO_MAX))}
                placeholder={t('bioPlaceholder')}
                maxLength={BIO_MAX}
                rows={3}
              />
              <div className="comment-char-counter" style={{ alignSelf: 'flex-end' }}>{bioInput.length}/{BIO_MAX}</div>

              <label className="edit-field-label">{t('instagramLabel')}</label>
              <div className="account-input-row account-input-row-handle">
                <span className="account-handle-prefix">@</span>
                <input
                  className="edit-field-input"
                  value={instagramInput}
                  onChange={e => setInstagramInput(e.target.value.slice(0, INSTAGRAM_MAX))}
                  placeholder={t('instagramPlaceholder')}
                  maxLength={INSTAGRAM_MAX}
                />
              </div>

              {editStatus.kind === 'error' && <p className="account-form-error">{editStatus.message}</p>}

              <div className="profile-edit-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => { setIsEditing(false); setEditStatus({ kind: 'idle' }); }}
                  disabled={editStatus.kind === 'pending'}
                >
                  {t('cancel')}
                </button>
                <button
                  type="button"
                  className="account-save-btn"
                  onClick={saveAll}
                  disabled={editStatus.kind === 'pending'}
                >
                  {editStatus.kind === 'pending' ? t('loading') : t('save')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {isBlocked ? (
        <div className="empty-state" style={{ marginTop: '2rem' }}>
          <i className="material-symbols-rounded empty-icon">block</i>
          <h3 style={{ margin: '0.5rem 0 0.25rem' }}>{t('profileBlocked')}</h3>
          <p style={{ color: 'var(--text-secondary)' }}>{t('profileBlockedAction')}</p>
        </div>
      ) : (
      <>
      {/* profile-tabs 를 덧붙여 반반 폭으로 — .feed-tabs 만 쓰면 왼쪽으로
         쏠린다. 별도 클래스인 이유는 휴면 중인 CommunityFeed 도 .feed-tabs 를
         쓰기 때문 (거기선 탭이 3개라 반반이 맞지 않는다). */}
      <div className="feed-tabs profile-tabs">
        <button
          className={`feed-tab ${tab === 'designs' ? 'active' : ''}`}
          onClick={() => setTab('designs')}
        >
          {t('profileDesigns')}
        </button>
        <button
          className={`feed-tab ${tab === 'collections' ? 'active' : ''}`}
          onClick={() => setTab('collections')}
        >
          {t('collectionsTab')}
        </button>
      </div>

      {tab === 'designs' ? (
        designs.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)' }}>{t('profileNoDesigns')}</p>
        ) : (
          <>
          <div className="feed-grid">
            {designs.map(d => {
              const thumb = gridImage(d);
              const styleLabel = styleName(d.style, d.spaceCategory);
              return (
                <button
                  key={d.id}
                  type="button"
                  className="edit-thumb-card"
                  onClick={() => navigate(`/s/${d.id}`)}
                >
                  {thumb && <img src={thumb} alt={d.name || styleLabel} className="edit-thumb-img" loading="lazy" onError={fallbackToFull(d)} />}
                  <div className="edit-thumb-meta">
                    <p className="edit-thumb-title">{d.name || styleLabel}</p>
                  </div>
                </button>
              );
            })}
          </div>
          {hasMoreDesigns && (
            <div className="controls">
              <button
                className="btn btn-secondary"
                onClick={loadMoreDesigns}
                disabled={loadingMoreDesigns}
              >
                {loadingMoreDesigns ? t('loadingDesigns') : t('loadMore')}
              </button>
            </div>
          )}
          </>
        )
      ) : (
        collections.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)' }}>
            {isSelf ? t('collectionEmpty') : t('collectionPublicEmpty')}
          </p>
        ) : (
          <div className="collections-grid">
            {collections.map(c => <CollectionCard key={c.id} collection={c} />)}
          </div>
        )
      )}
      </>
      )}

      {followListTab && (
        <FollowListModal
          uid={profile.uid}
          displayName={profileLabel(profile)}
          initialTab={followListTab}
          currentUser={user}
          onSignInRequest={onSignInRequest}
          onClose={() => setFollowListTab(null)}
        />
      )}
    </main>
  );
}
